import { useEffect, useMemo, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'

import {
  bulkAddRegistrations,
  deleteRegistration,
  deleteRegistrationList,
  updateRegistration,
  updateTournamentInfo,
} from '../lib/data.js'
import { refundEntryFee, refundPaypalEntry } from '../lib/payments.js'
import { LANES, enabledMethods } from '../lib/paymentLanes.js'
import { clubParam } from '../lib/tenant.js'
import { useDbValue } from '../lib/useDbValue.js'
import DataGate from '../components/DataGate.jsx'
import { parsePaste, buildPlayerHandicaps } from '../lib/registrationParse.js'
import { detectTeams, readSheetFile, pasteToGrid } from '../lib/importParse.js'
import { resolveAllowSelfReported } from '../lib/handicapEntry.js'

const METHODS = [
  { id: 'link',   label: 'Self-Reg Link / QR',  icon: '🔗' },
  { id: 'paste',  label: 'Paste from Email',    icon: '📋' },
  { id: 'file',   label: 'Import File',         icon: '📄' },
  { id: 'table',  label: 'Manual Table',        icon: '⌨️' },
  { id: 'prior',  label: 'From Prior Event',    icon: '🗂️' },
]

export default function RegistrationTab({ tournamentId, tournament }) {
  const [method, setMethod] = useState('link')
  const regs = tournament?.registrations || {}
  const info = tournament?.info || {}
  // Course params for per-player handicap capture on bulk import (STEP 3). The
  // authoritative net recompute happens at flight-lock, so unset params are fine.
  const hcpCtx = {
    slopeRating: Number(info.slopeRating),
    courseRating: Number(info.courseRating),
    coursePar: Number(info.coursePar),
    allowance: Number.isFinite(Number(info.handicapAllowance)) ? Number(info.handicapAllowance) : 100,
  }
  const allowSelf = resolveAllowSelfReported(info)

  const list = Object.entries(regs).map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => (a.registeredAt || 0) - (b.registeredAt || 0))

  const max = Number(info.maxTeams) || null
  const remaining = max ? Math.max(0, max - list.length) : null
  const deadline = info.entryDeadline ? new Date(info.entryDeadline) : null
  const daysToDeadline = deadline ? Math.ceil((deadline - new Date()) / 86400000) : null

  const exportCsv = () => {
    const lines = ['Team Name,Player 1,Player 2,Email,Phone,Handicap,Qualifying Score']
    for (const r of list) {
      lines.push([
        csv(r.teamName),
        csv(r.players?.[0]),
        csv(r.players?.[1]),
        csv(r.email),
        csv(r.phone),
        r.handicap ?? '',
        r.qualifyingScore ?? '',
      ].join(','))
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(info.name || 'tournament').replace(/\s+/g, '_')}_registrations.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div className="card grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        <Stat label="Registered" value={list.length} />
        <Stat label="Max Teams" value={max ?? '—'} />
        <Stat label="Spots Left" value={remaining ?? '—'} />
        <Stat label="Deadline" value={daysToDeadline == null ? '—' : daysToDeadline >= 0 ? `${daysToDeadline}d` : 'Passed'} />
      </div>

      <div className="card no-print">
        <div className="flex flex-wrap gap-2">
          {METHODS.map((m) => (
            <button
              key={m.id}
              onClick={() => setMethod(m.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${method === m.id ? 'bg-green-deep text-white' : 'bg-gray-100 text-gray-700'}`}>
              {m.icon} {m.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          {method === 'link'  && <SelfRegPanel tournamentId={tournamentId} />}
          {method === 'paste' && <PastePanel tournamentId={tournamentId} hcpCtx={hcpCtx} allowSelf={allowSelf} />}
          {method === 'file'  && <FilePanel tournamentId={tournamentId} hcpCtx={hcpCtx} allowSelf={allowSelf} />}
          {method === 'table' && <TablePanel tournamentId={tournamentId} hcpCtx={hcpCtx} allowSelf={allowSelf} />}
          {method === 'prior' && <PriorPanel tournamentId={tournamentId} />}
        </div>
      </div>

      <EntryFeeConfig tournamentId={tournamentId} info={info} />

      <div className="card">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-lg">Registered Teams ({list.length})</h3>
          <button className="btn-secondary text-xs" disabled={list.length === 0} onClick={exportCsv}>
            ⬇ Export CSV
          </button>
        </div>
        {list.length === 0 ? (
          <p className="text-gray-500 text-sm">No registrations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="text-left py-2">Team</th>
                  <th className="text-left py-2">Players</th>
                  <th className="text-left py-2">Contact</th>
                  <th className="text-right py-2">Status</th>
                  <th className="text-right py-2">Payment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <RegRow key={r.id} tournamentId={tournamentId} reg={r} info={info} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">{label}</div>
      <div className="text-2xl font-extrabold text-green-deep">{value}</div>
    </div>
  )
}

function RegRow({ tournamentId, reg, info }) {
  const offlineMethods = enabledMethods(info, 'offline')
  const [offMethod, setOffMethod] = useState(offlineMethods[0]?.key || 'cash')
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 font-semibold align-top">
        {reg.teamName}
        {reg.waitlisted ? <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Waitlist</span> : null}
        {reg.division ? <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{reg.division}</span> : null}
      </td>
      <td className="py-2 text-gray-600 align-top">{(reg.players || []).join(' / ')}</td>
      <td className="py-2 text-xs text-gray-500">
        {reg.email ? <div>{reg.email}</div> : null}
        {reg.phone ? <div>{reg.phone}</div> : null}
      </td>
      <td className="py-2 text-right">
        {(() => {
          // Registration entry status — manual toggle, Pending ↔ Received only.
          // (Qualifying/"Qualified" lives on the qualifying sheet, a later stage.)
          const received = reg.entryStatus === 'received'
          return (
            <button
              type="button"
              onClick={() => updateRegistration(tournamentId, reg.id, { entryStatus: received ? 'pending' : 'received' })}
              title="Click to toggle — entry received?"
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full transition cursor-pointer ${
                received ? 'bg-green-100 text-green-800 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}>
              {received ? 'Received' : 'Pending'}
            </button>
          )
        })()}
      </td>
      <td className="py-2 text-right">
        {(() => {
          const pay = reg.payment || {}
          const paid = pay.status === 'paid'
          const refunded = pay.status === 'refunded'
          const methodLabel = (LANES.flatMap((l) => l.methods).find((m) => m.key === pay.method)?.label)
            || (pay.provider === 'stripe' ? 'Stripe' : pay.provider === 'paypal' ? 'PayPal' : '')
          if (paid) {
            const isProcessor = pay.provider === 'stripe' || pay.provider === 'paypal'
            return (
              <div className="flex items-center justify-end gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                  Paid{methodLabel ? ` · ${methodLabel}` : ''}
                </span>
                <button
                  type="button"
                  className="text-[10px] font-bold uppercase tracking-wider text-red-700 hover:underline"
                  onClick={async () => {
                    if (pay.provider === 'stripe') {
                      if (confirm('Refund this Stripe payment (test mode)?')) {
                        try { await refundEntryFee(tournamentId, reg.id) } catch (e) { alert(e?.message || 'Refund failed.') }
                      }
                    } else if (pay.provider === 'paypal') {
                      if (confirm('Refund this PayPal payment (sandbox)?')) {
                        try { await refundPaypalEntry(tournamentId, reg.id) } catch (e) { alert(e?.message || 'Refund failed.') }
                      }
                    } else {
                      updateRegistration(tournamentId, reg.id, { payment: { status: 'unpaid' }, entryStatus: 'pending' })
                    }
                  }}>
                  {isProcessor ? 'Refund' : 'Mark Unpaid'}
                </button>
              </div>
            )
          }
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                {refunded ? 'Refunded' : 'Unpaid'}
              </span>
              {offlineMethods.length > 1 ? (
                <select
                  className="text-[11px] border border-gray-300 rounded px-1 py-0.5 bg-white text-gray-800"
                  value={offMethod}
                  onChange={(e) => setOffMethod(e.target.value)}>
                  {offlineMethods.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              ) : null}
              <button
                type="button"
                className="text-[10px] font-bold uppercase tracking-wider text-green-700 hover:underline"
                onClick={() => {
                  const method = offlineMethods.length ? offMethod : 'cash'
                  updateRegistration(tournamentId, reg.id, { payment: { status: 'paid', method, manual: true, paidAt: Date.now() }, entryStatus: 'received' })
                }}>
                Mark Paid
              </button>
            </div>
          )
        })()}
      </td>
      <td className="py-2 text-right">
        <button
          className="text-red-600 text-xs"
          onClick={() => {
            if (confirm(`Remove ${reg.teamName}?`)) deleteRegistration(tournamentId, reg.id)
          }}>×</button>
      </td>
    </tr>
  )
}

// Per-event entry fee + registration config. Writes to tournaments/{id}/info
// (entryFee / waitlistEnabled / registrationOpens / divisions) — the same info
// node the public /register page and the Stripe checkout function already read.
function EntryFeeConfig({ tournamentId, info }) {
  const [amount, setAmount] = useState(info?.entryFee?.amount ?? '')
  const [currency, setCurrency] = useState((info?.entryFee?.currency || 'USD').toUpperCase())
  const [waitlist, setWaitlist] = useState(!!info?.waitlistEnabled)
  const [opens, setOpens] = useState(info?.registrationOpens || '')
  const [divisions, setDivisions] = useState(Array.isArray(info?.divisions) ? info.divisions.join(', ') : '')
  const [ok, setOk] = useState(false)
  const [busy, setBusy] = useState(false)

  // Payment lanes config — lane on/off + per-method on/off. Seeded from
  // info.payments; defaults to everything off (additive — PBCC unaffected).
  const [payments, setPayments] = useState(() => {
    const out = {}
    for (const lane of LANES) {
      const saved = info?.payments?.[lane.key] || {}
      out[lane.key] = {
        enabled: !!saved.enabled,
        methods: Object.fromEntries(lane.methods.map((m) => [m.key, !!saved.methods?.[m.key]])),
      }
    }
    return out
  })
  const toggleLane = (laneKey, on) => {
    setOk(false)
    setPayments((p) => {
      const lane = LANES.find((l) => l.key === laneKey)
      const cur = p[laneKey]
      // Turning a lane ON with no methods chosen yet → default all its methods on.
      const anyMethod = Object.values(cur.methods).some(Boolean)
      const methods = (on && !anyMethod)
        ? Object.fromEntries(lane.methods.map((m) => [m.key, true]))
        : cur.methods
      return { ...p, [laneKey]: { enabled: on, methods } }
    })
  }
  const toggleMethod = (laneKey, methodKey, on) => {
    setOk(false)
    setPayments((p) => ({ ...p, [laneKey]: { ...p[laneKey], methods: { ...p[laneKey].methods, [methodKey]: on } } }))
  }

  const save = async () => {
    setBusy(true); setOk(false)
    try {
      const amt = Number(amount)
      await updateTournamentInfo(tournamentId, {
        entryFee: amt > 0 ? { amount: amt, currency: currency.toLowerCase() } : null,
        waitlistEnabled: waitlist,
        registrationOpens: opens || null,
        divisions: divisions.split(',').map((d) => d.trim()).filter(Boolean),
        payments,
      })
      setOk(true)
    } finally { setBusy(false) }
  }

  return (
    <div className="card">
      <h3 className="font-bold text-lg mb-3">Entry Fee &amp; Registration</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-gray-500">Entry Fee</span>
          <input className="input mt-1" type="number" min="0" step="0.01" value={amount}
            onChange={(e) => { setAmount(e.target.value); setOk(false) }} placeholder="0.00" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-gray-500">Currency</span>
          <select className="input mt-1" value={currency} onChange={(e) => { setCurrency(e.target.value); setOk(false) }}>
            {['USD', 'CAD', 'EUR', 'GBP', 'AUD'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-gray-500">Opens</span>
          <input className="input mt-1" type="date" value={opens}
            onChange={(e) => { setOpens(e.target.value); setOk(false) }} />
        </label>
        <label className="flex items-center gap-2 mt-5">
          <input type="checkbox" checked={waitlist} onChange={(e) => { setWaitlist(e.target.checked); setOk(false) }} />
          <span className="text-sm font-semibold text-gray-700">Waitlist when full</span>
        </label>
      </div>
      <label className="block mt-3">
        <span className="text-xs uppercase tracking-wider text-gray-500">Divisions</span>
        <input className="input mt-1" value={divisions}
          onChange={(e) => { setDivisions(e.target.value); setOk(false) }} placeholder="Championship, First, Senior" />
      </label>

      <div className="mt-4 border-t border-gray-200 pt-3 space-y-3">
        {LANES.map((lane) => {
          const on = payments[lane.key].enabled
          return (
            <div key={lane.key} className="rounded-lg border border-gray-200 p-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={on} onChange={(e) => toggleLane(lane.key, e.target.checked)} />
                <span className="text-sm font-bold text-gray-800">{lane.label}</span>
              </label>
              {on ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 pl-6">
                  {lane.methods.map((m) => (
                    <label key={m.key} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!!payments[lane.key].methods[m.key]}
                        onChange={(e) => toggleMethod(lane.key, m.key, e.target.checked)} />
                      <span className="text-sm text-gray-700">{m.label}</span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button className="btn-primary text-sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        {ok ? <span className="text-sm font-bold text-green-700">Saved ✓</span> : null}
      </div>
    </div>
  )
}

// -------------------- Method panels --------------------

function SelfRegPanel({ tournamentId }) {
  const url = `${window.location.origin}/register?t=${tournamentId}${clubParam()}`
  return (
    <div>
      <p className="text-sm text-gray-700">
        Print this QR code or share the link. Members can register themselves.
      </p>
      <div className="mt-3 flex items-center gap-4 flex-wrap">
        <div className="bg-white p-2 rounded-xl border-2 border-dashed border-gray-300">
          <QRCodeCanvas value={url} size={150} includeMargin />
        </div>
        <div className="text-sm">
          <div className="font-mono text-xs break-all bg-gray-50 px-2 py-1 rounded">{url}</div>
          <button
            className="btn-secondary text-xs mt-2"
            onClick={() => navigator.clipboard?.writeText(url)}>
            Copy Link
          </button>
        </div>
      </div>
    </div>
  )
}

// Preview label for a parsed row: per-player indexes when present, else the
// legacy team-level handicap.
function previewHcp(p) {
  if (Array.isArray(p.playerHandicaps)) {
    return `HCP ${p.playerHandicaps.map((e) => e?.handicapIndex ?? '—').join(' / ')}`
  }
  return `HCP ${p.handicap ?? '—'}`
}

// Warn when a GHIN-required event is being fed self-reported (bulk) indexes.
// Non-blocking per the current design — the values import but are flagged.
function SelfReportedWarn({ parsed, allowSelf }) {
  if (allowSelf) return null
  const n = (parsed || []).filter((p) => Array.isArray(p.playerHandicaps)).length
  if (n === 0) return null
  return (
    <p className="text-xs mt-2 rounded-lg px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
      ⚠ This event requires (GH)#-verified handicaps. {n} team{n === 1 ? '' : 's'} carry self-reported indexes — they'll import but are flagged unverified.
    </p>
  )
}

// Shared preview + import (used by Paste and Import File). Always shown before
// anything is committed — nothing wrong gets imported.
function PreviewCard({ rows, allowSelf, busy, onImport }) {
  return (
    <div className="mt-3 rounded-xl border border-gray-200 p-3">
      <p className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2">Preview · {rows.length} team{rows.length === 1 ? '' : 's'}</p>
      <div className="max-h-56 overflow-y-auto text-xs space-y-1">
        {rows.slice(0, 120).map((p, i) => (
          <div key={i} className="flex justify-between border-b last:border-0 py-1">
            <span><b>{p.teamName}</b> · {(p.players || []).join(' / ')}</span>
            <span className="text-gray-500">{p.email || previewHcp(p)}</span>
          </div>
        ))}
        {rows.length > 120 ? <div className="text-gray-400 italic">… {rows.length - 120} more</div> : null}
      </div>
      <SelfReportedWarn parsed={rows} allowSelf={allowSelf} />
      <button className="btn-primary mt-3" onClick={onImport} disabled={busy || rows.length === 0}>
        {busy ? 'Importing…' : `Import ${rows.length}`}
      </button>
    </div>
  )
}

function PastePanel({ tournamentId, hcpCtx, allowSelf }) {
  const [text, setText] = useState('')
  // Tabular paste (copied straight from Excel / Google Sheets → tab-separated)
  // runs through the smart detector; a plain "Name / Name" list uses parsePaste
  // (which handles slash, comma, tab, &, "and", and per-player "(8.3)").
  const parsed = useMemo(() => {
    if (!text.trim()) return []
    return /\t/.test(text) ? detectTeams(pasteToGrid(text), null, hcpCtx).rows : parsePaste(text, hcpCtx)
  }, [text, hcpCtx])
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(0)

  const importAll = async () => {
    if (parsed.length === 0) return
    setBusy(true)
    try {
      const n = await bulkAddRegistrations(tournamentId, parsed)
      setDone(n)
      setText('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <p className="text-sm text-gray-700 mb-2">
        Paste a team list — one team per line. Works with a slash, comma, tab, &amp;, or "and" between the two players (so copying straight from Excel or Google Sheets works too):
      </p>
      <ul className="text-xs text-gray-500 mb-2 list-disc list-inside space-y-0.5">
        <li><code>Walter Cash / Steve Scherrey</code></li>
        <li><code>Walter Cash, Steve Scherrey</code> <span className="text-gray-400">(or tab-separated from a sheet)</span></li>
        <li><code>Mark Ratliff (8.3) / Cole Keese (12)</code> <span className="text-gray-400">(per-player index)</span></li>
      </ul>
      <textarea
        className="input min-h-[140px] font-mono text-sm"
        placeholder={'Walter Cash / Steve Scherrey\nJackson May / Will Rose\n…'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {done > 0 ? (
        <p className="text-sm text-green-deep mt-2 font-bold">Imported {done} team{done === 1 ? '' : 's'}.</p>
      ) : null}
      {parsed.length > 0 ? <PreviewCard rows={parsed} allowSelf={allowSelf} busy={busy} onImport={importAll} /> : null}
    </div>
  )
}

// Manual column mapping — only shown when auto-detect can't tell which columns
// are the two players (e.g. a headerless sheet with a team column + two names).
function MappingStep({ columns, autoHeader, onApply }) {
  const [hasHeader, setHasHeader] = useState(!!autoHeader)
  const [mode, setMode] = useState('two-col')
  const [p1, setP1] = useState('')
  const [p2, setP2] = useState('')
  const [combinedCol, setCombinedCol] = useState('')
  const [team, setTeam] = useState('')
  const [email, setEmail] = useState('')

  const num = (v) => (v === '' ? null : Number(v))
  const opts = columns.map((c) => (
    <option key={c.index} value={c.index}>{c.label}{c.sample ? ` — ${c.sample}` : ''}</option>
  ))
  const apply = () => {
    const o = { hasHeader, team: num(team), email: num(email) }
    if (mode === 'combined') {
      if (combinedCol === '') return
      o.mode = 'combined'; o.combinedCol = Number(combinedCol)
    } else {
      if (p1 === '' || p2 === '') return
      o.mode = 'two-col'; o.p1 = Number(p1); o.p2 = Number(p2)
    }
    onApply(o)
  }

  return (
    <div className="mt-3 rounded-xl border p-3 space-y-2" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
      <p className="text-sm font-bold" style={{ color: '#92400E' }}>Couldn't auto-detect the players — map the columns:</p>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} /> First row is a header (skip it)</label>
      <div className="flex flex-wrap gap-4 text-xs">
        <label className="flex items-center gap-1"><input type="radio" checked={mode === 'two-col'} onChange={() => setMode('two-col')} /> Two player columns</label>
        <label className="flex items-center gap-1"><input type="radio" checked={mode === 'combined'} onChange={() => setMode('combined')} /> Both players in one column</label>
      </div>
      {mode === 'two-col' ? (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs block">Player 1<select className="input mt-0.5" value={p1} onChange={(e) => setP1(e.target.value)}><option value="">— pick —</option>{opts}</select></label>
          <label className="text-xs block">Player 2<select className="input mt-0.5" value={p2} onChange={(e) => setP2(e.target.value)}><option value="">— pick —</option>{opts}</select></label>
        </div>
      ) : (
        <label className="text-xs block">Players column (both names, e.g. "P1 / P2")<select className="input mt-0.5" value={combinedCol} onChange={(e) => setCombinedCol(e.target.value)}><option value="">— pick —</option>{opts}</select></label>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs block">Team Name (optional)<select className="input mt-0.5" value={team} onChange={(e) => setTeam(e.target.value)}><option value="">Auto (P1 / P2)</option>{opts}</select></label>
        <label className="text-xs block">Email (optional)<select className="input mt-0.5" value={email} onChange={(e) => setEmail(e.target.value)}><option value="">None</option>{opts}</select></label>
      </div>
      <button className="btn-secondary text-sm" onClick={apply}>Apply mapping →</button>
    </div>
  )
}

function FilePanel({ tournamentId, hcpCtx, allowSelf }) {
  const [grid, setGrid] = useState(null)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(0)
  const [err, setErr] = useState('')
  const [fileName, setFileName] = useState('')

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setErr(''); setDone(0); setResult(null); setGrid(null); setFileName(file.name)
    try {
      const g = await readSheetFile(file)
      if (!g.length) { setErr('That file looks empty.'); return }
      setGrid(g)
      setResult(detectTeams(g, null, hcpCtx))
    } catch (ex) {
      setErr(ex?.message || 'Could not read that file.')
    }
  }

  const applyMapping = (override) => setResult(detectTeams(grid, override, hcpCtx))

  const importAll = async () => {
    const rows = result?.rows || []
    if (!rows.length) return
    setBusy(true)
    try {
      const n = await bulkAddRegistrations(tournamentId, rows)
      setDone(n); setResult(null); setGrid(null); setFileName('')
    } finally {
      setBusy(false)
    }
  }

  const downloadTemplate = () => {
    const csv = 'Team Name,Player 1,Player 2,Email,Phone,Handicap 1,Handicap 2\nRatliff / Keese,Mark Ratliff,Cole Keese,mark@example.com,555-0123,8.3,12\n'
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    a.download = 'tourney_registrations_template.csv'
    a.click()
  }

  return (
    <div>
      <p className="text-sm text-gray-700">
        Upload a roster in any standard format — <b>Excel (.xlsx)</b>, <b>Google Sheets</b> (export as .xlsx or .csv), <b>CSV</b>, or <b>TSV</b>. The program figures out the columns; if it can't tell, it asks. You always see a preview before importing.
      </p>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} className="text-sm" />
        <button className="btn-ghost text-xs" onClick={downloadTemplate}>⬇ Download template</button>
      </div>
      {fileName ? <p className="text-xs text-gray-500 mt-1">Loaded: <b>{fileName}</b></p> : null}
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}
      {done > 0 ? <p className="text-sm text-green-deep mt-2 font-bold">Imported {done} team{done === 1 ? '' : 's'}.</p> : null}

      {result?.ambiguous ? (
        <MappingStep columns={result.columns} autoHeader={result.autoHeader} onApply={applyMapping} />
      ) : null}
      {result && !result.ambiguous && result.rows.length > 0 ? (
        <PreviewCard rows={result.rows} allowSelf={allowSelf} busy={busy} onImport={importAll} />
      ) : null}
      {result && !result.ambiguous && result.rows.length === 0 ? (
        <p className="text-sm text-gray-600 mt-2">No teams found in that file — check the layout, or the mapping step will let you point at the right columns.</p>
      ) : null}
    </div>
  )
}

function csv(s) { return s == null ? '' : `"${String(s).replace(/"/g, '""')}"` }

function TablePanel({ tournamentId, hcpCtx, allowSelf }) {
  const [rows, setRows] = useState(() => Array.from({ length: 6 }, () => ({ teamName: '', p1: '', p2: '', h1: '', h2: '', qual: '' })))
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(0)

  const update = (i, patch) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row))
  const addRow = () => setRows((r) => [...r, { teamName: '', p1: '', p2: '', h1: '', h2: '', qual: '' }])

  const onCellKey = (e, i) => {
    if (e.key === 'Enter' && i === rows.length - 1) {
      e.preventDefault()
      addRow()
    }
  }

  const submit = async () => {
    const cleaned = rows
      .filter((r) => r.p1.trim() || r.p2.trim() || r.teamName.trim())
      .map((r) => {
        // Keep names and their per-player indexes aligned in lockstep.
        const pairs = [[r.p1.trim(), r.h1], [r.p2.trim(), r.h2]].filter(([nm]) => nm)
        const players = pairs.map(([nm]) => nm)
        const ph = buildPlayerHandicaps(pairs.map(([, h]) => h), hcpCtx)
        return {
          teamName: r.teamName.trim() || (players.length === 2 ? `${players[0].split(' ').slice(-1)[0]} / ${players[1].split(' ').slice(-1)[0]}` : players[0] || ''),
          players,
          ...(ph ? { playerHandicaps: ph } : {}),
          qualifyingScore: r.qual === '' ? null : Number(r.qual),
        }
      })
    if (cleaned.length === 0) return
    setBusy(true)
    try {
      const n = await bulkAddRegistrations(tournamentId, cleaned)
      setDone(n)
      setRows(Array.from({ length: 6 }, () => ({ teamName: '', p1: '', p2: '', h1: '', h2: '', qual: '' })))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <p className="text-sm text-gray-700 mb-2">Tab to move between cells. Enter on the last row to add another. <span className="text-gray-500">HCP 1 / HCP 2 are each player's handicap index (e.g. 8.3).</span></p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase text-gray-500 font-bold tracking-widest">
            <tr>
              <th className="text-left py-1">Team Name</th>
              <th className="text-left py-1">Player 1</th>
              <th className="text-left py-1">Player 2</th>
              <th className="text-right py-1 w-16">HCP 1</th>
              <th className="text-right py-1 w-16">HCP 2</th>
              <th className="text-right py-1 w-16">Qual</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="py-1 pr-1"><input className="w-full text-sm border rounded px-2 py-1" value={r.teamName} onChange={(e) => update(i, { teamName: e.target.value })} onKeyDown={(e) => onCellKey(e, i)} /></td>
                <td className="py-1 pr-1"><input className="w-full text-sm border rounded px-2 py-1" value={r.p1} onChange={(e) => update(i, { p1: e.target.value })} onKeyDown={(e) => onCellKey(e, i)} /></td>
                <td className="py-1 pr-1"><input className="w-full text-sm border rounded px-2 py-1" value={r.p2} onChange={(e) => update(i, { p2: e.target.value })} onKeyDown={(e) => onCellKey(e, i)} /></td>
                <td className="py-1 pr-1"><input type="number" step="0.1" className="w-full text-sm border rounded px-2 py-1 text-right" value={r.h1} onChange={(e) => update(i, { h1: e.target.value })} onKeyDown={(e) => onCellKey(e, i)} /></td>
                <td className="py-1 pr-1"><input type="number" step="0.1" className="w-full text-sm border rounded px-2 py-1 text-right" value={r.h2} onChange={(e) => update(i, { h2: e.target.value })} onKeyDown={(e) => onCellKey(e, i)} /></td>
                <td className="py-1"><input type="number" className="w-full text-sm border rounded px-2 py-1 text-right" value={r.qual} onChange={(e) => update(i, { qual: e.target.value })} onKeyDown={(e) => onCellKey(e, i)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!allowSelf && rows.some((r) => r.h1 !== '' || r.h2 !== '') ? (
        <p className="text-xs mt-2 rounded-lg px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
          ⚠ This event requires (GH)#-verified handicaps. Typed indexes save as self-reported and are flagged unverified.
        </p>
      ) : null}
      <div className="flex gap-2 mt-3">
        <button className="btn-ghost text-xs" onClick={addRow}>+ Add row</button>
        <button className="btn-primary text-sm" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save All'}</button>
      </div>
      {done > 0 ? <p className="text-sm text-green-deep mt-2 font-bold">Saved {done} team{done === 1 ? '' : 's'}.</p> : null}
    </div>
  )
}

function PriorPanel({ tournamentId }) {
  // Robust load: surfaces errors, shows a spinner while connecting, and an
  // error + Retry instead of a silently-empty, unopenable dropdown.
  const { data: listsData, status, error, retry } = useDbValue('registrationLists')
  const lists = listsData || {}
  const [selectedId, setSelectedId] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const options = useMemo(() => (
    Object.entries(lists || {})
      .map(([id, v]) => ({
        id,
        name: v?.name || 'Saved List',
        count: Array.isArray(v?.teams) ? v.teams.length : 0,
        createdAt: v?.createdAt || 0,
      }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  ), [lists])

  const importList = async () => {
    if (!selectedId) return
    setBusy(true); setMsg('')
    try {
      const teams = lists?.[selectedId]?.teams || []
      const rows = teams.map((t) => ({
        teamName: t?.teamName || 'Team',
        players: Array.isArray(t?.players) ? t.players : [],
        email: t?.email || null,
        phone: t?.phone || null,
      }))
      const n = await bulkAddRegistrations(tournamentId, rows)
      setMsg(`Imported ${n} team${n === 1 ? '' : 's'}.`)
    } catch (e) {
      setMsg(e?.message || 'Import failed.')
    } finally {
      setBusy(false)
    }
  }

  const removeList = async () => {
    if (!selectedId) return
    if (!confirm('Delete this saved list?')) return
    await deleteRegistrationList(selectedId)
    setSelectedId('')
  }

  return (
    <DataGate status={status} error={error} retry={retry} label="Loading saved lists…">
      {options.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-4 text-center bg-gray-50">
          <p className="text-sm text-gray-600">
            <b>No saved lists yet.</b> Use <b>Save List</b> below to store a team list — it'll show up here to load into any event.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Load a previously saved team list into this event.</p>
          <div className="flex flex-wrap gap-2 items-center">
            <select className="input max-w-xs" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">Choose a saved list…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.count} teams</option>)}
            </select>
            <button className="btn-primary text-sm" disabled={!selectedId || busy} onClick={importList}>
              {busy ? 'Importing…' : 'Import'}
            </button>
            <button className="btn-ghost text-sm" style={{ color: '#B91C1C' }} disabled={!selectedId} onClick={removeList}>
              Delete saved list
            </button>
          </div>
          {msg ? <p className="text-xs text-gray-600">{msg}</p> : null}
        </div>
      )}
    </DataGate>
  )
}
